import { isDefined } from '~/types/typeGuards';

export interface SortInfo<T extends string = string> {
  sort: T;
  direction: 'asc' | 'desc';
}

type FilterOperatorType =
  | 'contains'
  | 'eq'
  | 'like'
  | 'lt'
  | 'gt'
  | 'in'
  | 'notIn'
  | 'lte'
  | 'gte'
  | 'ilike'
  | 'ilikeAnd'
  | 'empty'
  | 'neq'
  | '';

type Range = [number | null, number | null];
interface FilterInfo {
  key: string;
  operator: FilterOperatorType;
  value: string | number | string[] | number[] | boolean | Range;
}

export interface PaginationInfo {
  size: number;
  cursor?: string;
}

interface IJsonAPIParamsBuilder<T extends {}> {
  build: () => T;
}

export interface IJsonAPISortBuilder extends IJsonAPIParamsBuilder<{ sort: string }> {
  asc: (sort: string) => void;
  desc: (sort: string) => void;
}

type ExtraParamType = string | number | boolean;
interface IJsonAPIExtraQueryBuilder extends IJsonAPIParamsBuilder<Record<string, ExtraParamType>> {
  add: (key: string, value: ExtraParamType) => void;
}

interface IJsonAPIIncludeBuilder extends IJsonAPIParamsBuilder<{ include: string }> {
  add: (include: string) => void;
  set: (includeList: string[]) => void;
}

type JsonAPIFieldsType = Record<string, string>;
interface IJsonAPIFieldsBuilder extends IJsonAPIParamsBuilder<{
  fields: JsonAPIFieldsType;
}> {
  add: (resource: string, fields: string[]) => void;
}

interface JsonAPIPageType {
  size: number;
  cursor?: string;
}

export interface IJsonAPICursorPaginatorBuilder extends IJsonAPIParamsBuilder<{
  page: JsonAPIPageType;
}> {
  size: (value: number) => void;
  cursor: (value: string) => void;
}

type JsonAPIFilterType = Record<
  `${string}__${FilterOperatorType}`,
  string | number | boolean | string[] | number[] | Range
>;
export interface IJsonAPIFilterBuilder extends IJsonAPIParamsBuilder<{
  filter: JsonAPIFilterType;
}> {
  contains: (key: string, value: string) => void;
  eq: <T extends string | number | boolean>(key: string, value: T) => void;
  like: (key: string, value: string[]) => void;
  ilike: (key: string, value: string) => void;
  iLikeAnd: (key: string, value: string[]) => void;
  lt: (key: string, value: string | number) => void;
  lte: (key: string, value: string | number) => void;
  gt: (key: string, value: string | number) => void;
  gte: (key: string, value: string | number) => void;
  in: (key: string, value: string[] | number[]) => void;
  notIn: (key: string, value: string[] | number[]) => void;
  neq: (key: string, value: string | number) => void;
  empty: (key: string, value: boolean) => void;
  range: (key: string, value: Range) => void;
}

export type JsonAPIQueryType = {
  filter?: JsonAPIFilterType;
  fields?: JsonAPIFieldsType;
  page?: JsonAPIPageType;
  include?: string;
} & Record<string, ExtraParamType>;

export const jsonAPISortBuilder = (initialRules: SortInfo[] = []): IJsonAPISortBuilder => {
  const rules = initialRules;

  const addRule = (sort: string, direction: SortInfo['direction']) => {
    rules.push({
      direction,
      sort,
    });
  };

  const asc = (sort: string) => {
    addRule(sort, 'asc');
  };

  const desc = (sort: string) => {
    addRule(sort, 'desc');
  };

  const build = () => {
    const sort = rules.map((rule) => `${rule.direction === 'asc' ? '' : '-'}${rule.sort}`).join(',');
    return { sort };
  };

  return { asc, desc, build };
};

export const jsonAPIExtraQueryBuilder = (): IJsonAPIExtraQueryBuilder => {
  const queryEntries: { key: string; value: ExtraParamType }[] = [];

  const add = (key: string, value: ExtraParamType) => {
    queryEntries.push({
      key,
      value,
    });
  };

  const build = () => {
    const result = queryEntries.reduce<Record<string, ExtraParamType>>(
      (acc, entry) => ({
        ...acc,
        [entry.key]: entry.value,
      }),
      {},
    );
    return result;
  };

  return { add, build };
};

export const jsonAPIIncludeBuilder = (initialIncludeList: string[] = []): IJsonAPIIncludeBuilder => {
  const includeList = initialIncludeList;

  const add = (include: string) => {
    includeList.push(include);
  };

  const set = (includeList: string[]) => {
    includeList.length = 0;
    includeList.push(...includeList);
  };

  const build = () => {
    const includeStr = includeList.join(',');
    return { include: includeStr };
  };

  return { add, set, build };
};

export const jsonAPIFieldsBuilder = (
  initialConfig: {
    resource: string;
    fields: string[];
  }[] = [],
): IJsonAPIFieldsBuilder => {
  const fieldsConfig = initialConfig;

  const add = (resource: string, fields: string[]) => {
    if (fieldsConfig.some((conf) => conf.resource === resource)) {
      throw new Error(`Resource fields for ${resource} already added.`);
    }
    fieldsConfig.push({ resource, fields });
  };

  const build = () => {
    const fields = fieldsConfig.reduce<JsonAPIFieldsType>(
      (acc, config) => ({
        ...acc,
        [config.resource]: config.fields.join(','),
      }),
      {},
    );
    return { fields };
  };

  return { add, build };
};

export const jsonAPICursorPaginatorBuilder = (
  initialPagination: PaginationInfo = { size: 20 },
): IJsonAPICursorPaginatorBuilder => {
  const paginationInfo = initialPagination;

  const size = (size: number) => {
    paginationInfo.size = size;
  };

  const cursor = (cursor: string) => {
    paginationInfo.cursor = cursor;
  };

  const build = () => {
    const result: JsonAPIPageType = {
      size: paginationInfo.size,
    };
    if (isDefined(paginationInfo.cursor)) {
      result.cursor = paginationInfo.cursor;
    }
    return { page: result };
  };

  return { size, cursor, build };
};

export const jsonAPIFilterBuilder = (intialFilters: FilterInfo[] = []): IJsonAPIFilterBuilder => {
  const filters = intialFilters;

  const contains = (key: string, value: string) => {
    filters.push({
      key,
      operator: 'contains',
      value,
    });
  };

  const eq = (key: string, value: string | number | boolean) => {
    filters.push({
      key,
      operator: 'eq',
      value,
    });
  };

  const like = (key: string, value: string[]) => {
    filters.push({
      key,
      operator: 'like',
      value,
    });
  };

  const ilike = (key: string, value: string) => {
    filters.push({
      key,
      operator: 'ilike',
      value,
    });
  };

  const iLikeAnd = (key: string, value: string[]) => {
    filters.push({
      key,
      operator: 'ilikeAnd',
      value,
    });
  };

  const lt = (key: string, value: string | number) => {
    filters.push({
      key,
      operator: 'lt',
      value,
    });
  };

  const lte = (key: string, value: string | number) => {
    filters.push({
      key,
      operator: 'lte',
      value,
    });
  };

  const gt = (key: string, value: string | number) => {
    filters.push({
      key,
      operator: 'gt',
      value,
    });
  };

  const gte = (key: string, value: string | number) => {
    filters.push({
      key,
      operator: 'gte',
      value,
    });
  };

  const inFn = (key: string, value: string[] | number[]) => {
    filters.push({
      key,
      operator: 'in',
      value,
    });
  };

  const notIn = (key: string, value: string[] | number[]) => {
    filters.push({
      key,
      operator: 'notIn',
      value,
    });
  };

  const neq = (key: string, value: string | number) => {
    filters.push({
      key,
      operator: 'neq',
      value,
    });
  };

  const empty = (key: string, value: boolean) => {
    filters.push({
      key,
      operator: 'empty',
      value,
    });
  };

  const range = (key: string, value: Range) => {
    filters.push({
      key,
      operator: '',
      value,
    });
  };

  const build = () => {
    const filter = filters.reduce<JsonAPIFilterType>((acc, filter) => {
      const key: keyof JsonAPIFilterType = `${filter.key}__${filter.operator}`;

      if (['in', 'notIn'].includes(filter.operator)) {
        if (!Array.isArray(filter.value)) throw new Error('`in` and `notIn` filter expect a valid array');
        const value = filter.value.join(',');
        acc[key] = value;
      } else {
        const value = filter.value;
        acc[key] = value;
      }

      return acc;
    }, {});

    return { filter };
  };

  return {
    contains,
    eq,
    neq,
    in: inFn,
    notIn,
    gte,
    gt,
    lt,
    lte,
    like,
    ilike,
    iLikeAnd,
    empty,
    range,
    build,
  };
};

export const buildJsonAPIQueryParams = (
  ...builders: (
    | IJsonAPICursorPaginatorBuilder
    | IJsonAPIExtraQueryBuilder
    | IJsonAPIFilterBuilder
    | IJsonAPIIncludeBuilder
    | IJsonAPISortBuilder
    | IJsonAPIFieldsBuilder
  )[]
): JsonAPIQueryType => builders.reduce<JsonAPIQueryType>((acc, builder) => Object.assign(acc, builder.build()), {});
